import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { longitudSms, SMS_DE_PARTIDA, VARIABLES_SMS } from './smsTexto';

/**
 * Los dos textos de SMS de la empresa: lo que dice cada uno. Cuándo salen, y si
 * se usa el SMS, se decide en «Configuración» (`/ar-arranque`).
 *
 * Se guardan en la misma configuración que los días (`PUT /ar-arranque/settings`
 * con solo `sms.template_*`): el servidor conserva lo demás y comprueba el texto
 * (que diga `{acreedor}`, que no diga ClaimUp, que quepa en un SMS).
 *
 * No hay revisión de ClaimUp, como en el correo: en SMS nadie aprueba nada. Ver
 * docs/ar-sms-plan.md.
 */
const SMS = [
  ['first', 'Primer SMS', 'Texto del primer SMS'],
  ['second', 'Segundo SMS', 'Texto del segundo SMS'],
];

const PlantillasSms = () => {
  const [cfg, setCfg] = useState(null);
  const [textos, setTextos] = useState({ first: '', second: '' });
  const [guardando, setGuardando] = useState(false);

  const aplicar = useCallback(sms => {
    setCfg(sms);
    setTextos({ first: sms.template_first || '', second: sms.template_second || '' });
  }, []);

  useEffect(() => {
    api
      .get('/ar-arranque/settings')
      .then(({ data }) => aplicar(data.data.sms))
      .catch(err => toast.error(getErrorMessage(err)));
  }, [aplicar]);

  if (!cfg) return <p className='text-sm text-slate-500'>Cargando…</p>;

  const cambiado = textos.first !== (cfg.template_first || '') || textos.second !== (cfg.template_second || '');

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.put('/ar-arranque/settings', {
        sms: { template_first: textos.first, template_second: textos.second },
      });
      aplicar(data.data.sms);
      toast.success('Textos guardados');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className='space-y-4'>
      <p className='text-sm text-slate-500'>
        Salen desde un número de ClaimUp que tu deudor no conoce, así que cada texto tiene que decir quién escribe: pon{' '}
        {'{acreedor}'}, que se cambia por el nombre de tu empresa. Caben {cfg.max_length} caracteres, sin tildes salvo
        la é. El deudor puede contestar, y su respuesta entra en la revisión.{' '}
        {cfg.enabled ? (
          'El SMS está activado.'
        ) : (
          <>
            El SMS está apagado: actívalo en{' '}
            <Link to='/ar-arranque' className='text-indigo-600 hover:text-indigo-800'>
              Configuración
            </Link>
            , donde también eliges qué día sale cada uno.
          </>
        )}
      </p>

      {SMS.map(([clave, titulo, aria]) => {
        const texto = textos[clave];
        const largo = longitudSms(texto);
        return (
          <section
            key={clave}
            className='rounded-lg border border-slate-200 bg-white p-4 space-y-2'
            aria-label={titulo}
          >
            <h2 className='text-sm font-semibold text-slate-800'>{titulo}</h2>
            <textarea
              aria-label={aria}
              rows={3}
              value={texto}
              onChange={e => setTextos(t => ({ ...t, [clave]: e.target.value }))}
              className='block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm'
            />
            <div className='flex flex-wrap items-center gap-2 text-xs text-slate-500'>
              <span>Insertar:</span>
              {VARIABLES_SMS.map(v => (
                <button
                  key={v.name}
                  type='button'
                  title={v.label}
                  onClick={() => setTextos(t => ({ ...t, [clave]: `${t[clave]}{${v.name}}` }))}
                  className='rounded-lg border border-slate-200 px-2 py-1 font-mono text-slate-600 hover:bg-slate-50'
                >
                  {`{${v.name}}`}
                </button>
              ))}
              {!texto.trim() && (
                <button
                  type='button'
                  onClick={() => setTextos(t => ({ ...t, [clave]: SMS_DE_PARTIDA[clave] }))}
                  className='text-indigo-600 hover:text-indigo-800'
                >
                  Empezar con un ejemplo
                </button>
              )}
              <span className={`ml-auto ${largo > cfg.max_length ? 'text-red-600' : ''}`}>
                Unos {largo}/{cfg.max_length} con un nombre y un importe largos
              </span>
            </div>
          </section>
        );
      })}

      <button
        type='button'
        onClick={guardar}
        disabled={guardando || !cambiado}
        className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
      >
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
    </div>
  );
};

export default PlantillasSms;
